/**
 * Rede de segurança de renderização: sem um error boundary, qualquer exceção em um componente
 * (dado inesperado de uma conversa, por exemplo) desmontava a árvore React inteira e a tela
 * ficava em branco, sem pista do motivo. Aqui o erro fica contido na área da página: o menu
 * continua de pé, a causa aparece na tela e no console, e há como tentar de novo ou recarregar.
 *
 * O boundary é remontado a cada troca de rota (`resetKey`), então sair da tela quebrada
 * já limpa o estado de erro.
 */
import React, { type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router-dom";

interface BoundaryProps {
  resetKey: string;
  children: ReactNode;
}

interface BoundaryState {
  error: Error | null;
}

class Boundary extends React.Component<BoundaryProps, BoundaryState> {
  override state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("[plataforma] falha ao renderizar a tela", error, info.componentStack);
  }

  override componentDidUpdate(previous: BoundaryProps): void {
    if (previous.resetKey !== this.props.resetKey && this.state.error) this.setState({ error: null });
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;
    return (
      <div role="alert" className="mx-auto my-10 flex max-w-xl flex-col gap-3 rounded-panel border border-border bg-surface p-6 text-ink shadow-s1" data-status="render_error">
        <h2 className="m-0 text-lg font-extrabold">Esta tela não conseguiu ser exibida</h2>
        <p className="m-0 text-sm text-muted">Algo inesperado aconteceu ao montar a página. O resto da Plataforma continua funcionando.</p>
        <pre className="m-0 max-h-40 overflow-auto whitespace-pre-wrap rounded-control bg-surface-soft p-3 text-xs text-muted">{error.message}</pre>
        <div className="flex flex-wrap gap-2">
          <button type="button" className="rounded-control border border-border px-3 py-1.5 text-sm font-bold" onClick={() => this.setState({ error: null })}>
            Tentar de novo
          </button>
          <button type="button" className="rounded-control border border-border px-3 py-1.5 text-sm font-bold" onClick={() => window.location.reload()}>
            Recarregar a página
          </button>
        </div>
      </div>
    );
  }
}

export function RouteErrorBoundary({ children }: { children: ReactNode }): React.ReactElement {
  const location = useLocation();
  return <Boundary resetKey={`${location.pathname}${location.search}`}>{children}</Boundary>;
}
